import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { ccFromSlug, nameFromSlug, slugFromCC } from 'tf-post-parser';
import emojiFlags from 'emoji-flags';
import Markdown from 'markdown-to-jsx';
import Head from '../Header/Head';
import CountryMapPosts from './CountryMapPosts';
import BookingDesk from './BookingDesk';
import SectionHeading from './SectionHeading';
import FieldNotesPlate from './FieldNotesPlate';
import FieldNotesProse from './FieldNotesProse';
import GuidePostGrid from './GuidePostGrid';
import InlineBlogCta from '../Post/Article/InlineBlogCta';
import EditLocationDetails from 'components/General/EditLocationDetails';
import imageCrop from 'helpers/imageCrop';
import { MAPBOX_TOKEN } from '../../config';

export default function MiniTravelGuide(props) {
  const [bbox, setBbox] = useState(undefined);

  const { title, locationDetails, body, query, noIndex, breadcrumbs } = props;
  let { locationBox, subdivision, city } = query;
  if (!subdivision) subdivision = props.subdivision;
  if (!city) city = props.city;
  const country = query.country || slugFromCC(query.countryCode);
  const countryCode = query.countryCode || ccFromSlug(country);
  const countryName = nameFromSlug(country);

  const locationString = `${city ? `${city}, ` : ''}${
    subdivision ? `${subdivision}, ` : ''
  }${countryName}`;

  const generatedOgImage = `https://og.travelfeed.com/api/travelGuide?title=${encodeURIComponent(
    title,
  )}&image=${imageCrop(locationDetails.image, 2000)}`;

  useEffect(() => {
    if (!locationBox) {
      fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          locationString,
        )}.json?limit=1&language=en-GB&access_token=${MAPBOX_TOKEN}`,
      )
        .then(res => {
          return res.json();
        })
        .then(data => {
          if (data && data.features && data.features.length > 0) {
            setBbox(data.features[0].bbox);
          }
        });
    }
  }, [city, subdivision, countryName]);

  // Ancestors only — the plate's title is the current page.
  const crumbs = [{ name: 'Travel Guides', href: '/destinations' }];
  if (subdivision)
    crumbs.push({ name: countryName, href: `/destinations/${country}` });
  if (city)
    crumbs.push({
      name: subdivision,
      href: `/destinations/${country}/${encodeURIComponent(subdivision)}`,
    });

  const mapBbox = locationBox || bbox || locationDetails.bbox;

  return (
    <>
      <Head
        shorttitle={`${
          emojiFlags.countryCode(countryCode.toUpperCase()).emoji
        } ${title} Travel Guide (${new Date().getFullYear()})`}
        description={`The ultimate travel guide to ${title}: Your all-in-one travel resource for insider tips, itineraries, must-sees and hidden gems in ${title}.`}
        image={generatedOgImage}
        includeMapbox
        noIndex={noIndex}
        breadcrumbs={breadcrumbs}
      />

      <FieldNotesPlate
        image={locationDetails.image}
        blur={locationDetails.blur}
        alt={locationDetails.coverAlt || title}
        title={
          <>
            {title}
            <em className="tw-mt-1 tw-block tw-text-[0.42em] tw-font-medium tw-italic tw-leading-snug tw-text-primary-200">
              travel guide
            </em>
          </>
        }
        kicker={props.isLive ? 'LIVE FIELD GUIDE' : 'FIELD GUIDE'}
        breadcrumbs={crumbs}
        countryCode={countryCode}
        bbox={locationDetails.bbox}
        captionLeft={`${title} · Field notes · ${countryCode.toUpperCase()}`}
        locationDetails={locationDetails}
        editButton={
          !props.noEdit && (
            <EditLocationDetails
              countryCode={countryCode}
              subdivision={subdivision}
              city={city}
              data={locationDetails}
              triggerClassName="tw-text-[#5F6F69] hover:tw-text-primary-700"
            />
          )
        }
        compact
      />

      <div className="tw-mx-auto tw-max-w-5xl tw-px-4">
        <section className="tw-py-12 sm:tw-py-16">
          <FieldNotesProse
            dropCap
            centered
            live={props.isLive}
            isStreaming={props.isStreaming}
            hasBody={Boolean(body && body !== ' ')}
          >
            {body && body !== ' ' ? (
              <Markdown
                options={{
                  // Model-written text: HTML in it is shown as text, never
                  // rendered (a <script> in server-rendered HTML would run).
                  disableParsingRawHTML: true,
                  overrides: {
                    a: {
                      component: props => {
                        return props.children;
                      },
                    },
                    img: {
                      component: props => {
                        return props.alt;
                      },
                    },
                  },
                }}
              >
                {body}
              </Markdown>
            ) : (
              <></>
            )}
          </FieldNotesProse>
        </section>

        <section className="tw-pb-12 sm:tw-pb-16">
          <BookingDesk locationString={locationString} />
        </section>

        {mapBbox && (
          <section className="tw-pb-12 sm:tw-pb-16">
            <div className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E] tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)]">
              <div className="tw-flex tw-items-baseline tw-justify-between tw-px-5 tw-py-3 tw-font-mono tw-text-[10px] tw-font-semibold tw-tracking-[0.25em] tw-text-[#8A958F]">
                <span>THE MAP OF STORIES</span>
                <span className="tw-hidden sm:tw-inline">
                  {locationString.toUpperCase()}
                </span>
              </div>
              <CountryMapPosts bbox={mapBbox} countryCode={countryCode} />
            </div>
          </section>
        )}

        <p className="tw-m-0 tw-pb-10 tw-text-right tw-font-mono tw-text-[10px] tw-tracking-[0.18em] tw-text-[#8A958F]">
          THIS TRAVEL GUIDE MAY CONTAIN{' '}
          <Link
            href="/about/affiliate-disclosure"
            prefetch={false}
            className="tw-text-inherit tw-underline tw-decoration-dotted tw-underline-offset-2 hover:tw-text-primary-700"
          >
            AFFILIATE LINKS
          </Link>
        </p>

        <section className="tw-pb-16 sm:tw-pb-20">
          <SectionHeading eyebrow="The community">
            {locationDetails.title} Travel Blogs
          </SectionHeading>
          <GuidePostGrid
            query={query}
            about={title}
            cta={
              <InlineBlogCta
                countryCode={countryCode}
                thumbUrl={locationDetails.image}
              />
            }
          />
        </section>
      </div>
    </>
  );
}
